import React from 'react';

import { ArrowBottom } from '@crehana/icons';

import {
  Loader,
  TextLink,
  PrimaryButton,
  Container,
  Row,
  Col,
} from '@crehana/ui';

import { useMe } from '@crehana/web';

import TrailerPlayer from 'Jsx/Shared/TrailerPlayer/LazyTailerPlayer';
import StringParser from 'Jsx/Shared/StringParser';

import { enrollmentUrlWithSourceDetail } from '../../utils';
import { FreeCourseType } from '../../types';

import CourseStats from './CourseStats';

type IHeroSection = {
  course: FreeCourseType;
  showPhoneNumberDialog(enrollmentUrl: string): void;
};

const HeroSection: React.FC<IHeroSection> = ({
  course,
  showPhoneNumberDialog,
}) => {
  const {
    originalId,
    title,
    professor,
    promoImage,
    trailerSource,
    enrollments,
    score: { average, total },
    shortDescription,
    level: { name },
    duration,
  } = course;
  const CourseStatsComponent = (
    <CourseStats
      course={{
        enrollments,
        duration,
        score: {
          average,
          total,
        },
        level: {
          name,
        },
      }}
    />
  );
  const enrollmentUrl = enrollmentUrlWithSourceDetail.getUrl({
    originalId,
    enrollmentUrl: course.enrollmentUrl,
    sourceDetail: enrollmentUrlWithSourceDetail.SOURCES.FREE_COURSE,
  });

  const { loading: isLoadingUserData, me } = useMe();

  if (!course) {
    return null;
  }

  return (
    <div className="w-full py-32 bg-white md:bg-gray-lighter sm:py-48">
      <Container>
        <h2 className="font-label mb-16 sm:mb-24 text-base-ligher">
          Curso gratis del día
        </h2>
        <div className="md:hidden">
          <h2 className="font-h2 mb-16 md:mb-24">{title}</h2>
          {professor && (
            <p className="mb-16 sm:mb-12 font-body3">
              Por:{` `}
              <StringParser
                className="tw-font-semibold"
                string={professor.fullName}
                style
                Wrapper="span"
              />
            </p>
          )}
        </div>
        <Row className="flex-col-reverse items-start md:flex-row">
          <Col lg={5} md={5} xs={12}>
            <div className="relative flex flex-col items-start mt-16 sm:mt-0">
              <h2 className="font-h2 mb-16 md:mb-24 hidden md:block">
                {title}
              </h2>
              {professor && (
                <p className="mb-16 sm:mb-12 font-body3 hidden md:block">
                  Por:{` `}
                  <StringParser
                    className="tw-font-semibold"
                    string={professor.fullName}
                    style
                    Wrapper="span"
                  />
                </p>
              )}
              <p className="font-body3 mb-16 sm:mb-24 hidden sm:block">
                {shortDescription}
              </p>
              <div className="block md:hidden">{CourseStatsComponent}</div>
              {isLoadingUserData ? (
                <div className="mb-24">
                  <Loader size={24} />
                </div>
              ) : (
                <>
                  {me ? (
                    <>
                      <PrimaryButton
                        label="Accede 24 hrs gratis"
                        className="w-full mb-24 sm:w-auto"
                        onClick={() => {
                          if (me.phoneNumber && me.phoneNumber.length > 0) {
                            window.location.href = enrollmentUrl;
                          } else {
                            showPhoneNumberDialog(enrollmentUrl);
                          }
                        }}
                      />
                      <TextLink
                        label="Ver más cursos"
                        rightIcon={{ component: ArrowBottom, size: 14 }}
                        onClick={() => {
                          const courseAgendaSection = document.getElementById(
                            'course-agenda-section',
                          );

                          if (courseAgendaSection) {
                            courseAgendaSection.scrollIntoView({
                              behavior: 'smooth',
                            });
                          }
                        }}
                      />
                    </>
                  ) : (
                    <div className="mx-auto md:ml-0 flex flex-col items-center py-24 mt-16 md:mt-0 md:px-40 bg-white rounded-5">
                      <PrimaryButton
                        className="px-24 sm:px-40"
                        href={`/onboarding/?nextUrl=${enrollmentUrl}`}
                        label="Registrate y accede a 24hrs gratis"
                      />
                      <div className="mt-20">
                        <p className="mx-auto font-body3 text-center">
                          ¿Tienes una cuenta?{' '}
                          <a
                            className="tw-font-semibold text-green-main font-body4"
                            href={`/entrar/?nextUrl=${enrollmentUrl}`}
                          >
                            Inicia Sesión
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
          <Col
            lg={6}
            md={6}
            offset={{
              lg: 1,
              md: 1,
            }}
            xs={12}
          >
            <div className="flex flex-col">
              <TrailerPlayer
                courseId={originalId}
                title={title}
                promoImage={promoImage}
                trailerSource={trailerSource}
                withUserActivityTrack
              />
              <div className="hidden mt-24 md:block">
                {CourseStatsComponent}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HeroSection;
